/**
 * Clear Position Confirmation Dialog
 *
 * Displays a confirmation dialog before clearing (liquidating) a position.
 * Shows warning about market order slippage and lists actions that will be taken.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import type { StrategyInfo } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';

export interface ClearPositionDialogProps {
  open: boolean;
  strategy: StrategyInfo | null;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

export const ClearPositionDialog: React.FC<ClearPositionDialogProps> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();

  if (!strategy) return null;

  const symbol = strategy.execution_context.opportunity?.base ||
    strategy.execution_context.position?.symbol?.replace('USDT', '') || '-';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.danger,
        }}
      >
        {t('traderDashboard.clearPosition.title', { symbol })}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.bg.primary, pt: 3 }}>
        <Typography variant="body1" sx={{ mb: 2, color: theme.text.primary }}>
          {t('traderDashboard.clearPosition.confirmMessage')}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 2 }}>
          {t('traderDashboard.clearPosition.description')}
        </Typography>
        <Box component="ul" sx={{ color: theme.text.secondary, pl: 2 }}>
          <li>{t('traderDashboard.clearPosition.sellSpot')}</li>
          <li>{t('traderDashboard.clearPosition.closeFutures')}</li>
        </Box>
        <Alert severity="warning" sx={{ mt: 2, backgroundColor: 'rgba(210, 153, 34, 0.1)', color: theme.warning }}>
          {t('traderDashboard.clearPosition.warning')}
        </Alert>
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: theme.bg.primary,
          borderTop: `1px solid ${theme.border}`,
          p: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.text.secondary }} disabled={loading}>
          {t('traderDashboard.clearPosition.cancel')}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: theme.text.primary }} /> : null}
          sx={{
            backgroundColor: theme.danger,
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: theme.loss,
            },
          }}
        >
          {loading ? t('traderDashboard.clearPosition.clearing') : t('traderDashboard.clearPosition.clear')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClearPositionDialog;
